import { useQuery } from '@tanstack/react-query';
import { tenantApi } from '../api/tenantApi';
import { getCurrentEnvForApi } from '../api/apiBaseUrl';
import { useEnvOptional } from '../context/EnvContext';

/**
 * Caller's effective access for a tenant (mig 246). Drives read-only gating.
 *
 * `canWrite` is permissive when enforcement is off (nothing gates), and requires
 * write/super-admin when ACCESS_ENFORCEMENT is on — so the UI degrades gracefully
 * before the flag is flipped. `readOnly` is the inverse, only true under enforcement.
 */
export function useTenantAccess(tenantCode: string | undefined): {
  level: 'read' | 'write' | null;
  canWrite: boolean;
  readOnly: boolean;
  enforced: boolean;
  isSuperAdmin: boolean;
  isLoading: boolean;
} {
  // Grants are per environment and the request answers from the SELECTED env.
  // Keying by env stops a failed refetch after an env switch from leaving the
  // previous env's level on screen (React Query keeps prior data on error).
  const env = useEnvOptional()?.currentEnv ?? getCurrentEnvForApi();
  const { data, isLoading } = useQuery({
    queryKey: ['tenant-my-access', env, tenantCode],
    queryFn: () => tenantApi.myAccess(tenantCode as string),
    enabled: !!tenantCode,
    staleTime: 60_000,
  });

  const enforced = data?.enforced ?? false;
  const level = data?.level ?? null;
  const isSuperAdmin = data?.isSuperAdmin ?? false;
  const canWrite = !enforced || isSuperAdmin || level === 'write';

  return { level, canWrite, readOnly: enforced && !canWrite, enforced, isSuperAdmin, isLoading };
}
